import { USER_STATUSES, type UserStatus } from '../user-status';

/**
 * The legal moves of the member state machine (N9).
 *
 * Shared rather than kept inside the api because the admin portal renders the
 * actions a staff member may take on a user from exactly this map — a second
 * hand-maintained list in the UI would drift and offer buttons the server
 * refuses.
 *
 * Notes on the shape:
 * - **`banned` is terminal** — an empty list, deliberately. Reinstating a banned
 *   account is not a status transition; it is an appeal that creates a new
 *   decision trail (N20).
 * - **`suspended` can return to any non-terminal status**, because a suspension
 *   restores the member to whatever they were before it. Which one is legal for
 *   a given user is not expressible here — `UserStatusService` enforces that the
 *   restore target equals the recorded pre-suspension status.
 * - `pending_verification → profile_incomplete` is the reviewer's
 *   "changes requested" path; `rejected → pending_verification` is resubmission.
 */
export const USER_STATUS_TRANSITIONS: Readonly<Record<UserStatus, readonly UserStatus[]>> = {
  registered: ['profile_incomplete', 'suspended', 'banned'],
  profile_incomplete: ['pending_verification', 'suspended', 'banned'],
  pending_verification: ['approved', 'rejected', 'profile_incomplete', 'suspended', 'banned'],
  approved: ['pending_verification', 'suspended', 'banned'],
  rejected: ['pending_verification', 'profile_incomplete', 'suspended', 'banned'],
  suspended: [
    'registered',
    'profile_incomplete',
    'pending_verification',
    'approved',
    'rejected',
    'banned',
  ],
  banned: [],
};

/** Whether the state machine permits this move. A no-op move is not a transition. */
export function canTransition(from: UserStatus, to: UserStatus): boolean {
  return USER_STATUS_TRANSITIONS[from].includes(to);
}

/** The statuses reachable from here — what the admin UI turns into buttons. */
export function allowedTransitions(from: UserStatus): readonly UserStatus[] {
  return USER_STATUS_TRANSITIONS[from];
}

/** Every status the machine knows, for exhaustive iteration in seeds and specs. */
export const ALL_USER_STATUSES: readonly UserStatus[] = USER_STATUSES;
